/**
 * Token cost estimation for AI operations.
 * Catalog-driven: all model costs/metadata come from the server catalog,
 * with FALLBACK_CATALOG for immediate availability before fetch completes.
 */

import type { CostEstimate, TokenBalance, CatalogModel } from '@/types/ai.ts';

export const WELCOME_CREDITS = 250; // free credits on sign-up (matches ai-generate WELCOME_BONUS_TOKENS in _shared.ts; the server is authoritative)
export const TOKENS_PER_DOLLAR = 100; // 100 tokens = $1.00

// ─── Fallback catalog (mirrors edge function marked-up costs) ────────────

export const FALLBACK_CATALOG: CatalogModel[] = [
  { id: 'flux2-turbo',       category: 'image', name: 'FLUX.2 Turbo (Fast)',        cost: 3,  estSeconds: 8,  mode: 'sync',  resolution: null,    durationLimits: null,                sortOrder: 0 },
  { id: 'seedream',          category: 'image', name: 'Seedream 4.5 (Quality)',     cost: 6,  estSeconds: 25, mode: 'sync',  resolution: null,    durationLimits: null,                sortOrder: 1 },
  { id: 'nano-banana-2',     category: 'image', name: 'Nano Banana 2 (Best)',       cost: 12,  estSeconds: 15, mode: 'sync',  resolution: null,    durationLimits: null,                sortOrder: 2 },
  // Video is per-second: costPerSecond = retail credits/s; cost = 5s reference price.
  { id: 'h3-max',            category: 'video', name: 'MiniMax H3 Max',             cost: 50,  costPerSecond: 10, estSeconds: 20, mode: 'queue', resolution: '768p', durationLimits: { min: 5, max: 15 }, sortOrder: 0 },
  { id: 'h3-2k',             category: 'video', name: 'MiniMax H3 (2K)',            cost: 100, costPerSecond: 20, estSeconds: 60, mode: 'queue', resolution: '2K',   durationLimits: { min: 5, max: 15 }, sortOrder: 1 },
  { id: 'kokoro',            category: 'tts',   name: 'Kokoro (Fast)',               cost: 2,  estSeconds: 3,  mode: 'sync',  resolution: null,    durationLimits: null,                sortOrder: 0 },
  { id: 'elevenlabs',        category: 'tts',   name: 'ElevenLabs (Best)',           cost: 3,  estSeconds: 8,  mode: 'sync',  resolution: null,    durationLimits: null,                sortOrder: 1 },
  { id: 'nano-banana-2-touchup', category: 'touchup', name: 'Nano Banana 2 (Best)',  cost: 12,  estSeconds: 15, mode: 'sync',  resolution: null,    durationLimits: null,                sortOrder: 0 },
  { id: 'music',                category: 'audio',   name: 'ElevenLabs Music',         cost: 40, estSeconds: 30, mode: 'queue', resolution: null,    durationLimits: { min: 3, max: 15 },  sortOrder: 0 },
  { id: 'sfx',                  category: 'audio',   name: 'ElevenLabs SFX',           cost: 5,  estSeconds: 8,   mode: 'sync',  resolution: null,    durationLimits: { min: 1, max: 22 },  sortOrder: 1 },
  { id: 'tripo-prop',           category: '3d',      name: 'Tripo 3D Prop',             cost: 15, estSeconds: 60,  mode: 'queue', resolution: null,    durationLimits: null,                 sortOrder: 0 },
  { id: 'tripo-character',      category: '3d',      name: 'Tripo 3D Character',        cost: 26, estSeconds: 120, mode: 'queue', resolution: null,    durationLimits: null,                 sortOrder: 1 },
];

// ─── Catalog-driven helpers ──────────────────────────────────────────────

export function getModelFromCatalog(modelId: string, catalog: CatalogModel[]): CatalogModel | undefined {
  return catalog.find(m => m.id === modelId) ?? FALLBACK_CATALOG.find(m => m.id === modelId);
}

/** Cost in credits. For per-second video models, pass the shot's duration to get the real
 *  price (costPerSecond × clamped duration); without a duration the 5s reference price returns. */
export function getModelCost(modelId: string, catalog: CatalogModel[], durationSeconds?: number): number {
  const model = getModelFromCatalog(modelId, catalog);
  if (!model) return 0;
  if (model.costPerSecond != null && durationSeconds != null) {
    return Math.ceil(model.costPerSecond * clampDuration(durationSeconds, modelId, catalog));
  }
  return model.cost;
}

export function getModelEstSeconds(modelId: string, catalog: CatalogModel[], context?: string): number {
  // Prefer observed actual time from previous generations.
  const observed = getObservedSeconds(modelId, context);
  if (observed > 0) return Math.round(observed);
  return getModelFromCatalog(modelId, catalog)?.estSeconds ?? 30;
}

// ─── Client-side generation time tracking ──────────────────────────────
const GEN_TIMES_KEY = 'previs_gen_times';

/** Record the actual time a generation took for a model.
 *  Optional `context` separates single vs batch (e.g. 'single', 'batch'). */
export function recordGenerationTime(modelId: string, seconds: number, context?: string): void {
  try {
    const key = context ? `${modelId}:${context}` : modelId;
    const stored = JSON.parse(localStorage.getItem(GEN_TIMES_KEY) || '{}') as Record<string, number[]>;
    const times = stored[key] ?? [];
    times.push(Math.round(seconds));
    // Keep last 5 observations per key
    if (times.length > 5) times.splice(0, times.length - 5);
    stored[key] = times;
    localStorage.setItem(GEN_TIMES_KEY, JSON.stringify(stored));
  } catch { /* localStorage unavailable */ }
}

/** Get the average observed generation time for a model (0 if none).
 *  Optional `context` reads from a context-specific key. */
function getObservedSeconds(modelId: string, context?: string): number {
  try {
    const key = context ? `${modelId}:${context}` : modelId;
    const stored = JSON.parse(localStorage.getItem(GEN_TIMES_KEY) || '{}') as Record<string, number[]>;
    const times = stored[key];
    if (!times || times.length === 0) return 0;
    return Math.round(times.reduce((a, b) => a + b, 0) / times.length);
  } catch { return 0; }
}

/** Clamp a duration in seconds to a video model's supported range.
 *  If the model has discrete allowedDurations (e.g. Veo 3.1: [4, 6, 8]),
 *  snaps to the nearest permitted value. */
export function clampDuration(seconds: number, modelId: string, catalog: CatalogModel[]): number {
  const model = getModelFromCatalog(modelId, catalog);
  if (!model?.durationLimits) return Math.round(seconds);
  const { min, max } = model.durationLimits;
  const clamped = Math.max(min, Math.min(max, Math.round(seconds)));

  // Snap to discrete allowed values if the model requires it
  const allowed = model.allowedDurations;
  if (allowed && allowed.length > 0) {
    let best = allowed[0];
    let bestDist = Math.abs(clamped - best);
    for (const v of allowed) {
      const dist = Math.abs(clamped - v);
      if (dist < bestDist) { best = v; bestDist = dist; }
    }
    return best;
  }

  return clamped;
}

/** Get the duration limits for a model */
export function getDurationLimits(modelId: string, catalog: CatalogModel[]): { min: number; max: number } | null {
  return getModelFromCatalog(modelId, catalog)?.durationLimits ?? null;
}

/** Estimate total cost for N items with a given model */
export function estimateCost(count: number, modelId: string, catalog: CatalogModel[]): CostEstimate {
  const perShot = getModelCost(modelId, catalog);
  const totalTokens = count * perShot;
  return {
    perShot,
    totalShots: count,
    totalTokens,
    formatted: `~${totalTokens} credits`,
  };
}

/** Format token amount as dollar string (e.g. 10 tokens → "$0.10") */
export function tokensToDollars(tokens: number): string {
  const dollars = tokens / TOKENS_PER_DOLLAR;
  return `$${dollars.toFixed(2)}`;
}

// ─── Token packs ─────────────────────────────────────────────────────────

export const TOKEN_PACKS = [
  { id: 'pack_100',  tokens: 100,  priceCents: 500,  label: 'Starter' },
  { id: 'pack_400',  tokens: 400,  priceCents: 1500, label: 'Standard', badge: 'Most Popular' as const },
  { id: 'pack_1200', tokens: 1200, priceCents: 4000, label: 'Pro', badge: 'Best Value' as const },
] as const;

export type TokenPack = typeof TOKEN_PACKS[number];

/** Return the highest-cost image model from the catalog (best quality). */
export function getBestImageModel(catalog: CatalogModel[]): CatalogModel {
  const effective = catalog.length > 0 ? catalog : FALLBACK_CATALOG;
  return effective
    .filter(m => m.category === 'image')
    .sort((a, b) => b.cost - a.cost)[0];
}

export function canAfford(balance: TokenBalance, cost: number): boolean {
  if (balance.isBeta) return true;
  return balance.available >= cost;
}

export function formatTokens(balance: TokenBalance): string {
  if (balance.isBeta) return '\u221E available (beta)';
  return `${balance.available.toLocaleString()} credits`;
}

// ─── Purchase dialog helpers ────────────────────────────────────────────────

export const LOW_BALANCE_THRESHOLD = 20;

export interface PackOutputEstimate {
  enhancedImages: number;
  videos: number;
}

/** Concrete output counts for a credit amount — drives value framing in purchase dialog */
export function getOutputEstimates(credits: number, catalog: CatalogModel[]): PackOutputEstimate {
  const cheapestImage = catalog.filter(m => m.category === 'image').sort((a, b) => a.cost - b.cost)[0]
    ?? FALLBACK_CATALOG.find(m => m.category === 'image')!;
  const cheapestVideo = catalog.filter(m => m.category === 'video').sort((a, b) => a.cost - b.cost)[0]
    ?? FALLBACK_CATALOG.find(m => m.category === 'video')!;
  return {
    enhancedImages: Math.floor(credits / cheapestImage.cost),
    videos: Math.floor(credits / cheapestVideo.cost),
  };
}

/** Per-action cost reference for "What credits buy" section — built dynamically from catalog */
export function buildCreditCostTable(catalog: CatalogModel[]): Array<{ action: string; cost: number }> {
  const effective = catalog.length > 0 ? catalog : FALLBACK_CATALOG;
  return effective
    .slice()
    .sort((a, b) => {
      const catOrder: Record<string, number> = { image: 0, video: 1, tts: 2, audio: 3 };
      if (a.category !== b.category) return (catOrder[a.category] ?? 9) - (catOrder[b.category] ?? 9);
      return a.sortOrder - b.sortOrder;
    })
    .map(m => ({ action: m.name, cost: m.cost }));
}
